import React, { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { T, stringFormat } from 'src/shared/helpers';
import { OutlineButton } from 'src/components/widgets/buttons';
import InspectorShell, { InspectorSubjectKind, InspectorUsage } from './inspectorShell';
import { SceneEditorAction } from './sceneEditorActionBar';

// IL PANNELLO DI UN MUCCHIO. Con più di una riga presa, la domanda «cosa sto guardando» ha una
// risposta al plurale, e il pannello che descrive un soggetto solo non sa darla: mostrava quello
// su cui era caduto l'ultimo clic, cioè una delle sette cose scelte, come se le altre sei non
// esistessero.
//
// DICE CHE COSA HAI PRESO, E COSA PUOI FARNE. La testata dice «Multiple elements» e il badge «N
// selected» apre l'elenco — con l'icona di specie su ogni voce, perché un mucchio può essere fatto di
// cose diverse —: premere una voce prende quella sola (round 4, pacchetto C, come nel prototipo).
// Nel corpo, i comandi che valgono per tutti.
//
// I COMANDI SONO GLI STESSI DELLA BARRA, non una seconda grammatica: qui hanno la parola scritta
// accanto al glifo perché c'è la larghezza per scriverla, e perché un comando che agisce su sette
// cose insieme è il momento sbagliato per farsi indovinare da un'icona. Un motore solo, due porte.

// DUE ELENCHI, DUE MESTIERI. Un MUCCHIO PRESO è un recinto: dice che cosa hai in mano adesso, e
// il filo intorno con i divisori fra le righe è ciò che lo rende una cosa sola invece di un
// elenco che continua. Il contenuto di un GRUPPO no: quelle righe si premono, e una gabbia
// intorno a delle porte le fa sembrare un riquadro di sola lettura — nel mockup infatti non c'è.
const List = styled.ul<{ $plain?: boolean }>`
    margin: 0 0 4px;
    border: ${(props) => (props.$plain ? 'none' : `1px solid ${props.theme.app.borderColor}`)};
    border-radius: 6px;
    padding: 0;
    max-height: 260px;
    overflow-y: auto;
    list-style: none;
`;

const Entry = styled.li<{ $plain?: boolean }>`
    color: ${(props) => props.theme.app.textColor};
    font-family: ${(props) => props.theme.app.font};
    font-size: 12px;

    & + & {
        border-top: ${(props) => (props.$plain ? 'none' : `1px solid ${props.theme.app.borderColor}`)};
    }
`;

// LA RIGA HA DUE FORME PERCHÉ HA DUE MESTIERI, e il disegno è lo stesso: dentro un mucchio
// preso dice soltanto che cosa hai in mano — non c'è dove andare, sono tutte lì — mentre dentro
// un gruppo è anche la PORTA per i suoi, e allora si preme. La forma la decide chi costruisce
// la riga, passando o non passando `onPick`.
const entryBody = css`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 5px 8px;

    svg {
        flex: none;
        width: 14px;
        height: 14px;
    }

    /* Le icone Material del rail si riempiono; i glifi della scena restano a contorno. */
    svg:not([stroke]) {
        fill: currentColor;
    }
`;

const EntryStatic = styled.span`
    ${entryBody}
`;

const EntryPick = styled.button`
    ${entryBody}
    width: 100%;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: ${(props) => props.theme.app.component.button.primary.hover.backgroundColor};
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: -2px;
    }
`;

const EntryName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const SelectionCommands = styled.div<{ $flush?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: ${(props) => (props.$flush ? '0' : '12px')};
`;

// IL COMANDO È L'`OutlineButton` DELLO STORYBOOK (utente, 2026-10-14), nella misura compatta della
// colonna: bordo grigio, fondo bianco, e passandoci sopra si accende solo il bordo. Lo usa anche il
// Composer per la sua selezione multipla, così i due pannelli parlano con lo stesso bottone.
// A TUTTA COLONNA E AL CENTRO: un comando che prende la riga intera non ha un vicino da cui farsi
// allineare, e il testo appoggiato a sinistra di uno spazio vuoto lascia il bottone sbilanciato.
export const SelectionCommand = styled(OutlineButton).attrs({ type: 'button' })`
    width: 100%;
    height: auto;
    min-height: 0;
    gap: 8px;
    border-radius: 6px;
    padding: 7px 10px;
    font-family: ${(props) => props.theme.app.font};
    font-size: 12px;

    > svg {
        flex: none;
        width: 14px;
        height: 14px;
    }
`;

export interface SelectionEntry {
    id: string;
    name: string;
    icon?: ReactNode;
    /** Presente: la riga è una porta, e premerla porta al suo soggetto. In un mucchio, prende quella sola. */
    onPick?: () => void;
}

export interface SelectionInspectorProps {
    entries: SelectionEntry[];
    /** Gli stessi comandi della barra: qui ricevono la selezione invece di una riga. */
    actions: SceneEditorAction[];
    /**
     * UN INSIEME CHE HA UN NOME PROPRIO — un gruppo di materiali — invece di un mucchio nato da
     * un clic. Cambia solo la testata: sotto, le due cose sono la stessa — un elenco di soggetti e
     * i comandi che valgono per tutti — e per questo non sono due componenti.
     */
    name?: string;
    kind?: InspectorSubjectKind;
    specs?: string;
    onRename?: (name: string) => void;
    /** L'icona della sezione nel rail: la testata dice da dove viene il mucchio. */
    icon?: ReactNode;
    /** La specie delle righe del mucchio: colora il badge «N selected». */
    tone?: InspectorSubjectKind;
}

const SelectionInspector: FC<SelectionInspectorProps> = ({ entries, actions, name, kind, specs, onRename, icon, tone }) => {
    // Basta che UNA riga sia una porta: un elenco mezzo premibile e mezzo no non esiste.
    const pickable = entries.some((entry) => entry.onPick);
    // UN MUCCHIO NON HA UN NOME PROPRIO: l'elenco sta nel badge della testata, non nel corpo.
    const pile = name === undefined;
    const selected: InspectorUsage[] | undefined = pile
        ? [
              {
                  label: stringFormat(T._('{0} selected', 'Admin'), entries.length.toString()),
                  tone,
                  entries: entries.map((entry) => ({ value: entry.id, label: entry.name, mark: entry.icon })),
                  onPick: (value) => entries.find((entry) => entry.id === value)?.onPick?.()
              }
          ]
        : undefined;

    return (
        <InspectorShell
            kind={kind ?? 'selection'}
            name={name ?? T._('Multiple elements', 'Admin')}
            specs={specs}
            usage={selected}
            onRename={onRename}
            icon={icon}
        >
            {!pile && (
                <List $plain={pickable}>
                    {entries.map((entry) =>
                        entry.onPick ? (
                            <Entry key={entry.id} $plain>
                                <EntryPick type='button' onClick={entry.onPick}>
                                    {entry.icon}
                                    <EntryName title={entry.name}>{entry.name}</EntryName>
                                </EntryPick>
                            </Entry>
                        ) : (
                            <Entry key={entry.id}>
                                <EntryStatic>
                                    {entry.icon}
                                    <EntryName title={entry.name}>{entry.name}</EntryName>
                                </EntryStatic>
                            </Entry>
                        )
                    )}
                </List>
            )}

            <SelectionCommands $flush={pile}>
                {actions.map((action) => (
                    <SelectionCommand key={action.name} $danger={action.kind === 'danger'} disabled={action.disabled} onClick={action.onClick}>
                        {action.icon}
                        <span>{action.name}</span>
                    </SelectionCommand>
                ))}
            </SelectionCommands>
        </InspectorShell>
    );
};

export default SelectionInspector;
